/**
 * Career Openings — city tab switching + mobile card slider.
 *
 * Desktop (>1024px): tabs switch vertical card panels as before.
 * Tablet/Mobile (≤1024px): each panel's cards become a horizontal Swiper
 * slider so users can swipe through job cards. Swiper is initialized /
 * destroyed on breakpoint change via matchMedia so no library overhead on
 * desktop.
 */

import Swiper from 'swiper';
import { FreeMode } from 'swiper/modules';

const SLIDER_BREAKPOINT = 1024; // matches $bp-tablet-lg

export function initCareerOpenings() {
	document.querySelectorAll( '[data-career-openings]' ).forEach( ( block ) => {
		if ( block.dataset.thriveCareerOpeningsInit === '1' ) return;
		block.dataset.thriveCareerOpeningsInit = '1';

		const tabs   = Array.from( block.querySelectorAll( '[data-career-tab]' ) );
		const panels = Array.from( block.querySelectorAll( '[data-career-panel]' ) );

		if ( ! tabs.length || ! panels.length ) return;

		// panel el → Swiper instance
		const swiperMap = new Map();

		// ── Slide wrap / unwrap helpers ────────────────────────────────────

		function wrapCards( panel ) {
			// Grab direct card children (and empty-state element)
			const children = Array.from( panel.children ).filter(
				( el ) =>
					el.classList.contains( 'block-career-openings__card' ) ||
					el.classList.contains( 'block-career-openings__empty' )
			);
			if ( ! children.length ) return;

			const wrapper = document.createElement( 'div' );
			wrapper.className = 'swiper-wrapper';
			panel.insertBefore( wrapper, children[ 0 ] );
			children.forEach( ( child ) => {
				child.classList.add( 'swiper-slide' );
				wrapper.appendChild( child );
			} );
		}

		function unwrapCards( panel ) {
			const wrapper = panel.querySelector( ':scope > .swiper-wrapper' );
			if ( ! wrapper ) return;
			Array.from( wrapper.children ).forEach( ( child ) => {
				child.classList.remove( 'swiper-slide' );
				panel.appendChild( child );
			} );
			wrapper.remove();
		}

		// ── Swiper init / destroy per panel ───────────────────────────────

		function initPanelSwiper( panel ) {
			if ( swiperMap.get( panel ) ) return;
			wrapCards( panel );
			const swiper = new Swiper( panel, {
				modules: [ FreeMode ],
				slidesPerView: 'auto',
				spaceBetween: 24,
				freeMode: { enabled: true, sticky: true },
				grabCursor: true,
				observer: true,
				observeParents: true,
			} );
			swiperMap.set( panel, swiper );
		}

		function destroyPanelSwiper( panel ) {
			const swiper = swiperMap.get( panel );
			if ( ! swiper ) return;
			swiper.destroy( true, true );
			swiperMap.delete( panel );
			unwrapCards( panel );
		}

		function initAllSwipers()    { panels.forEach( initPanelSwiper ); }
		function destroyAllSwipers() { panels.forEach( destroyPanelSwiper ); }

		// ── Tab switching ──────────────────────────────────────────────────

		tabs.forEach( ( tab ) => {
			tab.addEventListener( 'click', () => {
				const key = tab.dataset.careerTab;

				tabs.forEach( ( t ) => {
					const active = t === tab;
					t.classList.toggle( 'is-active', active );
					t.setAttribute( 'aria-selected', active ? 'true' : 'false' );
				} );

				panels.forEach( ( panel ) => {
					const active = panel.dataset.careerPanel === key;
					panel.classList.toggle( 'is-active', active );
					if ( active ) {
						panel.removeAttribute( 'hidden' );
						// Let the browser paint before updating Swiper
						// so it can measure the now-visible panel width.
						const s = swiperMap.get( panel );
						if ( s ) requestAnimationFrame( () => s.update() );
					} else {
						panel.setAttribute( 'hidden', '' );
					}
				} );
			} );
		} );

		// ── Responsive handling ────────────────────────────────────────────

		const mq = window.matchMedia( `(max-width: ${ SLIDER_BREAKPOINT }px)` );

		function handleMQ( e ) {
			if ( e.matches ) initAllSwipers();
			else             destroyAllSwipers();
		}

		mq.addEventListener( 'change', handleMQ );
		handleMQ( mq );
	} );
}
